// ============================================
// Yfirlestur.is - Íslensk stafsetningarprófun
// ============================================
//
// Notar GreynirCorrect vélina frá Yfirlestur.is
// til að athuga stafsetningu og málfræði í íslensku.
//
// API: https://yfirlestur.is/correct.api
// Aðferð: POST
// Inntak: text=<texti>
// Úttök: JSON með leiðréttingum

const YFIRLESTUR_API = 'https://yfirlestur.is/correct.api';

// Villukóðar og lýsingar á íslensku
const ERROR_TYPE_LABELS = {
  // Stafsetningarvillur
  S001: 'Stafsetningarvilla',
  S002: 'Stafsetningarvilla',
  S003: 'Stafsetningarvilla',
  S004: 'Stafsetningarvilla',
  S005: 'Stafsetningarvilla',
  // Málfræðivillur
  P_WRONG_CASE_þgf_þf: 'Rangt fall',
  P_WRONG_CASE_nf_þf: 'Rangt fall',
  P_WRONG_CASE_þf_nf: 'Rangt fall',
  P_wrong_person: 'Röng persónubeygja',
  P_wrong_gender: 'Rangt kyn',
  P_wrong_number: 'Röng tölubeygja',
  // Tvöfölduð orð
  C001: 'Endurtekið orð',
  C002: 'Endurtekið orð',
  C003: 'Endurtekið orð',
  // Hástafavillur
  U001: 'Hástafavilla',
  // Greinarmerki
  W001: 'Greinarmerki',
};

/**
 * Flokkar villu eftir kóða
 */
function getErrorCategory(code) {
  if (!code) return 'villa';
  if (code.startsWith('S')) return 'stafsetning';
  if (code.startsWith('P')) return 'malfraedi';
  if (code.startsWith('C')) return 'endurtekning';
  if (code.startsWith('U')) return 'hastafur';
  if (code.startsWith('W')) return 'greinarmerki';
  return 'villa';
}

/**
 * Fær lesanlegt heiti villutegund
 */
function getErrorLabel(code) {
  return ERROR_TYPE_LABELS[code] || 'Villa';
}

/**
 * Kallar á Yfirlestur.is API og skilar leiðréttingum.
 *
 * @param {string} text - Texti til að athuga
 * @returns {Promise<{corrections: Array, correctedText: string, hasErrors: boolean}>}
 */
export async function checkIcelandicText(text) {
  if (!text || text.trim().length < 2) {
    return { corrections: [], correctedText: text, hasErrors: false };
  }

  try {
    const response = await fetch(YFIRLESTUR_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: `text=${encodeURIComponent(text)}`,
    });

    if (!response.ok) {
      console.warn('Yfirlestur.is API villa:', response.status);
      return { corrections: [], correctedText: text, hasErrors: false, apiError: true };
    }

    const data = await response.json();

    if (!data.valid || !data.result) {
      return { corrections: [], correctedText: text, hasErrors: false };
    }

    const corrections = [];
    let correctedText = text;

    // Vinna úr öllum setningum í niðurstöðunni
    for (const sentenceGroup of data.result) {
      for (const sentence of sentenceGroup) {
        if (!sentence.annotations || sentence.annotations.length === 0) {
          continue;
        }

        for (const annotation of sentence.annotations) {
          const correction = {
            // Upprunalegi textinn sem inniheldur villuna
            original: getOriginalText(sentence, annotation),
            // Tillaga að leiðréttingu
            suggestion: annotation.suggest || '',
            // Staðsetning í textanum
            startChar: annotation.start_char,
            endChar: annotation.end_char,
            // Tegund villu
            code: annotation.code,
            category: getErrorCategory(annotation.code),
            label: getErrorLabel(annotation.code),
            // Lýsing á villu
            description: annotation.text || '',
            // Nánari upplýsingar
            detail: annotation.detail || '',
          };

          // Bara bæta við ef tillaga er til
          if (correction.suggestion) {
            corrections.push(correction);
          }
        }

        // Nota leiðréttan texta frá API ef til staðar
        if (sentence.corrected && sentence.corrected !== sentence.original) {
          correctedText = sentence.corrected;
        }
      }
    }

    return {
      corrections,
      correctedText,
      hasErrors: corrections.length > 0,
    };
  } catch (error) {
    // Ef API er ekki aðgengilegt, ekki blokka notanda
    console.warn('Yfirlestur.is API villa:', error.message);
    return { corrections: [], correctedText: text, hasErrors: false, apiError: true };
  }
}

/**
 * Nær í upprunalegan texta sem inniheldur villuna
 */
function getOriginalText(sentence, annotation) {
  // Reyna fyrst að finna upprunalegan texta úr tokens
  if (sentence.tokens) {
    const startToken = annotation.start;
    const endToken = annotation.end;
    if (startToken !== undefined && endToken !== undefined) {
      const relevantTokens = sentence.tokens.filter(
        (t, idx) => idx >= startToken && idx <= endToken
      );
      if (relevantTokens.length > 0) {
        return relevantTokens.map(t => t.o.trim()).join(' ');
      }
    }
  }

  // Fallback: nota start_char og end_char
  if (sentence.original && annotation.start_char !== undefined && annotation.end_char !== undefined) {
    return sentence.original.substring(annotation.start_char, annotation.end_char);
  }

  return annotation.suggest || '';
}

/**
 * Debounce fall - bíður þar til notandi hættir að skrifa
 *
 * @param {Function} fn - Fall til að keyra
 * @param {number} delay - Tími í millisekúndum
 * @returns {Function} - Debounced fall
 */
export function debounce(fn, delay = 500) {
  let timer = null;
  return function (...args) {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      fn.apply(this, args);
      timer = null;
    }, delay);
  };
}

/**
 * Beitir einni leiðréttingu á texta
 *
 * @param {string} text - Upprunalegur texti
 * @param {object} correction - Leiðrétting til að beita
 * @returns {string} - Leiðréttur texti
 */
export function applyCorrection(text, correction) {
  if (!correction.original || !correction.suggestion) {
    return text;
  }
  // Skipta út fyrsta tilfelli af villunni
  return text.replace(correction.original, correction.suggestion);
}

/**
 * Beitir öllum leiðréttingum á texta
 *
 * @param {string} text - Upprunalegur texti
 * @param {Array} corrections - Fylki af leiðréttingum
 * @returns {string} - Leiðréttur texti
 */
export function applyAllCorrections(text, corrections) {
  let result = text;
  // Beita leiðréttingum frá enda til byrjunar til að staðsetningar haldi
  const sorted = [...corrections].sort((a, b) => (b.startChar || 0) - (a.startChar || 0));
  for (const correction of sorted) {
    result = applyCorrection(result, correction);
  }
  return result;
}
